import { z } from 'zod';
import { firestoreTimestampSchema } from './user'; // Re-use from user.ts
import { 
  optionalEmailFieldSchema, 
  genericNameFieldSchema, 
  phoneFieldSchema,
  emailFieldSchema, // For required emails in arrays
  businessNameSchema,
  shortBusinessNameSchema,
  optionalGenericNameFieldSchema, // Added import
  optionalPhoneFieldSchema,       // Added import
  optionalWebsiteFieldSchema      // Added import
} from '@/utils/validationRules';

// --- Common Schemas (internal to this module if not used elsewhere, or export if needed) ---
const permissionSchema = z.object({
  name: z.string(),
  checked: z.boolean(),
  id: z.string(),
  description: z.string().optional(),
}).passthrough();

export const memberSchema = z.object({
  userId: z.string(),
  role: z.string(), // TODO: Should use MemberRoleSchema from commonSchema if applicable
  status: z.string(), // TODO: Should use MemberStatusSchema from commonSchema if applicable
  permissions: z.array(permissionSchema).optional().nullable(),
  email: optionalEmailFieldSchema.nullable(),
  firstName: genericNameFieldSchema("First name").optional().nullable(),
  lastName: genericNameFieldSchema("Last name").optional().nullable(),
  phone: phoneFieldSchema.optional().nullable(),
  invitedAt: firestoreTimestampSchema, // Use the preprocessed schema directly
  joinedAt: firestoreTimestampSchema,  // Use the preprocessed schema directly
  addedAt: firestoreTimestampSchema,   // Use the preprocessed schema directly
  addedViaPortfolio: z.string().optional().nullable(),
}).passthrough();
export type Member = z.infer<typeof memberSchema>; // Export Member type

const coordinatesSchema = z.object({
  latitude: z.number(), // Made required
  longitude: z.number(), // Made required
  lat: z.number().optional(),
  lng: z.number().optional(),
}).optional().nullable(); // Removed .passthrough()


// --- Nested Schemas for Business ---

// Service Schemas
export const serviceItemSchema = z.object({
  name: z.string(),
  targeted: z.boolean().optional(),
  depth: z.number().optional(),
  parentId: z.string().optional().nullable(),
  includedLabel: z.string().optional().nullable(),
  treeName: z.string().optional(),
  labels: z.array(z.string()).optional().nullable(),
  id: z.string(),
  offered: z.boolean().optional(),
  children: z.array(z.lazy(() => serviceItemSchema)).optional().nullable(),
});
export const actualServicesDataObjectSchema = z.object({
  services: z.array(serviceItemSchema).optional().default([]),
  quickFilters: z.record(z.boolean()).optional().nullable(),
  hasBeenSaved: z.boolean().optional().nullable(),
});
export type ActualBusinessServicesData = z.infer<typeof actualServicesDataObjectSchema>;
export const servicesDataSchema = actualServicesDataObjectSchema.optional().nullable();

// Profitability & Calculated Metrics Schemas (internal for now)
const profitabilityMetricsSchema = z.object({
  requiredCloseRate: z.number().optional().nullable(),
  estCloseRate: z.number().optional().nullable(),
  requiredRevenuePerJob: z.number().optional().nullable(),
  estCostPerLead: z.number().optional().nullable(),
  estRevenuePerJob: z.number().optional().nullable(),
}).passthrough();
const calculatedMetricsSchema = z.object({
  creditAmount: z.number().optional().nullable(),
  numberOfJobs: z.number().optional().nullable(),
  reoccurringAmount: z.number().optional().nullable(),
  estRoas: z.number().optional().nullable(),
  totalCharge: z.number().optional().nullable(),
  grossEstRevenue: z.number().optional().nullable(),
  proratedAmount: z.number().optional().nullable(),
  leadsToBreakeven: z.number().optional().nullable(),
  additionalLeadsNeeded: z.number().optional().nullable(),
  grossMonthlyCost: z.number().optional().nullable(),
  numberOfLeads: z.number().optional().nullable(),
  discountAmount: z.number().optional().nullable(),
  proratedDiscount: z.number().optional().nullable(),
}).passthrough();

// Promotion Schemas
export const promotionSchema = z.object({
  amount: z.number().optional().nullable(),
  serviceGroupName: z.string().optional().nullable(),
  order: z.number().optional().nullable(),
  serviceId: z.string().optional().nullable(),
  isBasic: z.boolean().optional().nullable(),
  title: z.string().optional().nullable(),
  enabled: z.boolean().optional().nullable(),
  id: z.string(), // Made ID required for promotions
  serviceGroupId: z.string().optional().nullable(),
  type: z.string().optional().nullable(),
  description: z.string().optional().nullable(),
  serviceName: z.string().optional().nullable(),
  // Added missing optional fields
  minOrderValue: z.number().optional().nullable(),
  promoCode: z.string().optional().nullable(),
  startDate: z.string().optional().nullable(),
  endDate: z.string().optional().nullable(),
  occasion: z.string().optional().nullable(),
});
export const actualPromotionDataObjectSchema = z.object({
  showAll: z.boolean().optional().nullable(),
  promotions: z.array(promotionSchema).optional().default([]),
  hasBeenSaved: z.boolean().optional().nullable(),
});
export type ActualBusinessPromotionData = z.infer<typeof actualPromotionDataObjectSchema>;
export const promotionDataSchema = actualPromotionDataObjectSchema.optional().nullable();

// Differentiator Schemas
export const differentiatorSchema = z.object({
  category: z.string().optional().nullable(),
  id: z.string(),
  name: z.string(),
  selectionType: z.string().optional().nullable(),
  parentId: z.string().optional().nullable(),
});
export const actualDifferentiatorDataObjectSchema = z.object({
  yearsExperience: z.number().optional().nullable(),
  selectedDifferentiators: z.array(differentiatorSchema).optional().default([]),
  hasBeenSaved: z.boolean().optional().nullable(),
});
export type ActualBusinessDifferentiatorData = z.infer<typeof actualDifferentiatorDataObjectSchema>;
export const differentiatorDataSchema = actualDifferentiatorDataObjectSchema.optional().nullable();

// Business Hours & Lead Delivery Schemas (internal for now, export if needed by other contexts)
const businessHoursDaySchema = z.object({
  open: z.boolean().default(false),
  openTime: z.string().default("09:00"), // Default to 9 AM
  closeTime: z.string().default("17:00"), // Default to 5 PM
}); // Removed .passthrough()

// Helper to create a default business hours object for all 7 days
const defaultWeekBusinessHours = {
  monday: businessHoursDaySchema.parse({}), // Use parse to get default values
  tuesday: businessHoursDaySchema.parse({}),
  wednesday: businessHoursDaySchema.parse({}),
  thursday: businessHoursDaySchema.parse({}),
  friday: businessHoursDaySchema.parse({}),
  saturday: businessHoursDaySchema.parse({ open: false }), // Default Saturday to closed
  sunday: businessHoursDaySchema.parse({ open: false }),   // Default Sunday to closed
};

const baseLeadDeliveryDataSchema = z.object({
  customBusinessHours: z.boolean().default(false),
  emailDelivery: z.boolean().default(false),
  phoneDelivery: z.boolean().default(false),
  emailUsers: z.array(z.string()).optional().default([]),
  phoneUsers: z.array(z.string()).optional().default([]),
  emails: z.array(emailFieldSchema).optional().default([]), // Array of required-format, non-empty emails
  phones: z.array(phoneFieldSchema).optional().default([]), // Array of required-format, non-empty phones
  hasBeenSaved: z.boolean().default(false),
  businessHours: z.object({
    monday: businessHoursDaySchema.optional().default(defaultWeekBusinessHours.monday),
    tuesday: businessHoursDaySchema.optional().default(defaultWeekBusinessHours.tuesday),
    wednesday: businessHoursDaySchema.optional().default(defaultWeekBusinessHours.wednesday),
    thursday: businessHoursDaySchema.optional().default(defaultWeekBusinessHours.thursday),
    friday: businessHoursDaySchema.optional().default(defaultWeekBusinessHours.friday),
    saturday: businessHoursDaySchema.optional().default(defaultWeekBusinessHours.saturday),
  sunday: businessHoursDaySchema.optional().default(defaultWeekBusinessHours.sunday),
}).optional().default(defaultWeekBusinessHours),
}); // Removed .passthrough()
export { baseLeadDeliveryDataSchema }; // Export for use in defaults

export const leadDeliveryDataSchema = baseLeadDeliveryDataSchema.optional().nullable();
export type ActualBusinessLeadDeliveryData = z.infer<typeof baseLeadDeliveryDataSchema>; // For direct use if needed

// Location Schemas
// Schema for TargetedState, based on the interface in locationTypes.ts
export const targetedStateSchema = z.object({ // Export this schema
  value: z.string(),
  label: z.string(),
  targeted: z.boolean(),
});
export type InferredTargetedState = z.infer<typeof targetedStateSchema>; // Export inferred type

export const locationByRadiusSchema = z.object({ // Export this schema
  name: z.string(),
  targeted: z.boolean().optional(),
  coordinates: coordinatesSchema,
  state: z.string().optional(),
  zip: z.string().optional().nullable(),
  zipCodes: z.array(z.string()).optional().nullable(),
  id: z.string().optional(),
  serviceable: z.boolean().default(true), // Make required with a default
  stateAbr: z.string().optional(),
  googleCriteriaId: z.number().optional().nullable(), // Added
  targetType: z.string().optional().nullable(), // Added
}).passthrough();
export type InferredLocationByRadius = z.infer<typeof locationByRadiusSchema>; // Export inferred type

// This was the old locationDataSchema, will be replaced by the new structure below.
// const oldLocationDataSchema = z.object({
//   targetAllUS: z.boolean().optional().nullable(),
//   locationsByRadius: z.array(locationByRadiusSchema).optional().nullable(),
//   businessCoordinates: coordinatesSchema,
//   hasBeenSaved: z.boolean().optional().nullable(),
//   radiusMiles: z.number().optional().nullable(),
// }).passthrough(); // Ensure the commented out block is fully commented or removed.

// Internal schema for the actual location data object
export const actualLocationDataObjectSchema = z.object({ // Export this schema
  targetAllUS: z.boolean().optional().default(false), // Default to false, output is boolean
  locationsByRadius: z.array(locationByRadiusSchema).optional().default([]),
  businessCoordinates: coordinatesSchema.optional().nullable(), 
  hasBeenSaved: z.boolean().optional().nullable(), // Stays optional, can be undefined or null
  radiusMiles: z.number().optional().default(25), // Default to 25, output is number
  // New fields from BusinessLocationData interface
  targetedStates: z.array(targetedStateSchema).optional().default([]),
  targetedCounties: z.record(z.array(z.string())).optional().default({}),
  blockedZipCodes: z.array(z.string()).optional().default([]),
});
export type ActualBusinessLocationData = z.infer<typeof actualLocationDataObjectSchema>;

// Schema for the locationData field on Business (can be optional or null)
export const locationDataSchema = actualLocationDataObjectSchema.optional().nullable();

// This seems to be a duplicate or an old definition,
// there's a dedicated src/schemas/invitation.ts.
// For now, updating this one if it's used internally.
// const internalInvitationSchema = z.object({  // Commenting out as it might be unused, focus on exported one below
//   email: optionalEmailFieldSchema.nullable(),
// }).passthrough();


// --- Main Business Schema ---

// The search result indicated an exported invitationSchema here.
// If it's still present and used, it should be updated.
// However, the full file content provided after the last successful write
// does NOT show an `export const invitationSchema` at this location, only the
// `internalInvitationSchema` I added and then the `businessSchema`.
// This means the `export const invitationSchema` might have been removed or was a misinterpretation
// of the search result snippet.

// I will proceed assuming the file content I have is correct and does not have
// a separate `export const invitationSchema` just before `businessSchema`.
// The next step will be to update `src/schemas/invitation.ts`.

// Define BusinessStatus enum for Zod
export const businessStatusSchema = z.enum(["active", "onboarding", "paused", "archived", "pending_verification", "pending_approval", "disabled"]); // Added more common statuses
export type BusinessStatus = z.infer<typeof businessStatusSchema>;

// Define BusinessRole enum for Zod
export const businessRoleEnum = z.enum(['Primary Owner', 'Owner', 'Manager', 'Admin']);
export type BusinessRole = z.infer<typeof businessRoleEnum>;

// --- Stripe Schemas ---
export const stripePaymentMethodSchema = z.object({
  id: z.string(), // Stripe PaymentMethod ID (e.g., pm_xxxxxxxx)
  brand: z.string(),
  last4: z.string(),
  expMonth: z.number(),
  expYear: z.number(),
  isDefault: z.boolean().default(false),
  createdAt: firestoreTimestampSchema,
});
export type StripePaymentMethod = z.infer<typeof stripePaymentMethodSchema>;

export const stripeDataDocumentSchema = z.object({
  stripeCustomerId: z.string().nullable().optional(),
  defaultStripePaymentMethodId: z.string().nullable().optional(),
  paymentMethods: z.array(stripePaymentMethodSchema).optional().default([]),
  lastSyncStatus: z.enum(['success', 'error', 'pending_customer_creation', 'pending_customer_update', 'pending']).nullable().optional(),
  lastSyncTimestamp: firestoreTimestampSchema.nullable().optional(),
  errorMessage: z.string().nullable().optional(),
});
export type StripeDataDocument = z.infer<typeof stripeDataDocumentSchema>;

// New goCampaign schema
export const goCampaignServicesSchema = z.object({
  offered: z.array(z.string()).optional().default([]),
  targeted: z.array(z.string()).optional().default([]),
});

// Schema for an individual location identified by a Google Criteria ID
export const goCampaignCriteriaIdLocationSchema = z.object({
  id: z.number(), // The Google Criteria ID
  name: z.string().optional(), // Optional: human-readable name for this ID
  targeted: z.boolean().default(false), // Is this location actively targeted?
  serviceable: z.boolean().default(false) // Is this targeted location serviceable?
});
export type GoCampaignCriteriaIdLocation = z.infer<typeof goCampaignCriteriaIdLocationSchema>;

// Schema for an individual location identified by a name (e.g., for DMAs or broader areas not having a criteria ID)
export const goCampaignNamedLocationSchema = z.object({
  name: z.string(), // The name of the location (e.g., "Los Angeles, CA DMA")
  targeted: z.boolean().default(false),
  serviceable: z.boolean().default(false)
  // Potentially add 'type' here: 'dma', 'city_group', etc.
});
export type GoCampaignNamedLocation = z.infer<typeof goCampaignNamedLocationSchema>;

// Schema for GoCampaign Financials
export const goCampaignFinancialsSchema = z.object({
  budget: z.number().positive({ message: "Budget must be a positive number." }).optional().nullable(),
  profitabilityMetrics: profitabilityMetricsSchema.optional(), // Reuses existing schema
  calculatedMetrics: calculatedMetricsSchema.optional(),   // Reuses existing schema
  startDate: z.union([firestoreTimestampSchema, z.string().datetime().optional()]).optional().nullable(), // Added startDate here
});
export type GoCampaignFinancials = z.infer<typeof goCampaignFinancialsSchema>;

export const goCampaignSchema = z.object({
  services: goCampaignServicesSchema.optional(),
  locations: z.object({
    googleCriteriaIdLocations: z.array(goCampaignCriteriaIdLocationSchema).optional().default([]),
    targetedNamedLocations: z.array(goCampaignNamedLocationSchema).optional().default([]),
    countryNames: z.array(z.string()).optional().default([]),
    stateNames: z.array(z.string()).optional().default([]),
    blockedZipCodes: z.array(z.string().length(5, { message: "Zip code must be 5 digits" }).regex(/^\d{5}$/, { message: "Zip code must be 5 digits" })).optional().default([])
  }).optional(),
  differentiators: z.object({
    yearsOfExperience: z.number().optional().nullable(), // Added .nullable()
    diffIds: z.array(z.string()).optional().default([])
  }).optional(),
  leadDelivery: baseLeadDeliveryDataSchema.omit({ hasBeenSaved: true }).optional(), // Add leadDelivery, omit hasBeenSaved
  promotions: z.array(promotionSchema).optional(), // Added promotions, removed .default([])
  promotionsShowAll: z.boolean().optional(), // Added promotionsShowAll
  financials: goCampaignFinancialsSchema.optional(), // Added financials section
});

export const businessSchema = z.object({
  id: z.string(), // Add the document ID to the schema
  name: businessNameSchema,
  displayName: z.string().trim().optional().nullable(),
  shortName: optionalGenericNameFieldSchema("Short business name", 2, 30).optional().nullable(), // Use optional version
  status: businessStatusSchema.optional().nullable(), // Use the Zod enum, make optional or provide default
  address: z.string().trim().optional().nullable(),
  city: z.string().trim().optional().nullable(),
  state: z.string().trim().optional().nullable(),
  zip: z.string().trim().optional().nullable(),
  coordinates: coordinatesSchema,
  phone: optionalPhoneFieldSchema.optional().nullable(), // Use optional version
  email: optionalEmailFieldSchema.nullable(),
  website: optionalWebsiteFieldSchema.optional().nullable(), // Use optional version
  portfolioId: z.string(),
  industry: z.string().trim().optional().nullable(),
  industryId: z.string().optional().nullable(),
  industryVertical: z.string().optional().nullable(),
  founded: z.string().optional().nullable(),
  package: z.string().optional().nullable(),
  budget: z.number().optional().nullable(),
  monthlyBudget: z.number().optional().nullable(),
  members: z.array(memberSchema).optional().nullable(),
  servicesData: servicesDataSchema,
  profitabilityMetrics: profitabilityMetricsSchema.optional().nullable(),
  calculatedMetrics: calculatedMetricsSchema.optional().nullable(),
  promotionData: promotionDataSchema,
  differentiatorData: differentiatorDataSchema,
  leadDeliveryData: leadDeliveryDataSchema.optional().nullable(),
  locationData: locationDataSchema.optional().nullable(),
  checkoutCompleted: z.boolean().optional().nullable(),
  goCampaignCompleted: z.boolean().optional().nullable(),
  startDate: z.union([firestoreTimestampSchema, z.string().datetime().optional()]).optional().nullable(),
  lastDateUpdate: z.union([firestoreTimestampSchema, z.string().datetime().optional()]).optional().nullable(),
  timeZone: z.string().optional().nullable(),
  createdAt: firestoreTimestampSchema, 
  updatedAt: firestoreTimestampSchema,
  // Fields accessed in BusinessCard via PortfolioSectionContent that were not explicitly in schema
  title: z.string().optional().nullable(), // Alternative display name
  role: businessRoleEnum.optional().nullable(), // User's role in this specific business context if applicable
  showActions: z.boolean().optional().nullable(),
  logoUrl: z.string().url().optional().nullable(),
  // New gocampaign field
  gocampaign: goCampaignSchema.optional(),
  // Stripe data subdocument
  stripeData: stripeDataDocumentSchema.optional().nullable(),
}).passthrough(); // Keep passthrough on main business schema for flexibility during dev

export type Business = z.infer<typeof businessSchema>;

// --- Subcollection Schemas ---
export const businessVersionSchema = z.object({
  versionNumber: z.number().optional(),
  status: z.enum(["draft", "scheduled", "published"]),
  fields: z.record(z.any()).optional(),
  scheduledTime: firestoreTimestampSchema,
  createdAt: firestoreTimestampSchema,
  updatedAt: firestoreTimestampSchema,
}).passthrough();
export type BusinessVersion = z.infer<typeof businessVersionSchema>;

const adSpendMetricsSchema = z.object({
  average_cpc: z.number().optional().nullable(),
  clicks: z.number().optional().nullable(),
  cost: z.number().optional().nullable(),
  currency_code: z.string().optional().nullable(),
  average_cpm: z.number().optional().nullable(),
  conversions: z.number().optional().nullable(),
  conversion_value: z.number().optional().nullable(),
  impressions: z.number().optional().nullable(),
  ctr: z.number().optional().nullable(),
}).passthrough();

export const adSpendDaySchema = z.object({
  date: z.string(),
  metrics: adSpendMetricsSchema,
  syncTime: firestoreTimestampSchema,
  override: z.object({ cost: z.number().optional() }).passthrough().optional().nullable(),
}).passthrough();
export type AdSpendDay = z.infer<typeof adSpendDaySchema>;

export const leadSchema = z.object({
  campaign: z.string().optional().nullable(),
  caller_number: z.string().optional().nullable(),
  business_number: z.string().optional().nullable(),
  called_at: z.string().optional().nullable(),
  duration: z.number().optional().nullable(),
  status: z.string().optional().nullable(),
  source: z.string().optional().nullable(),
}).passthrough();
export type Lead = z.infer<typeof leadSchema>;

export const privateMappingsSchema = z.object({
  adId: z.string().optional().nullable(),
  ctmId: z.string().optional().nullable(),
  margin: z.number().optional().nullable(),
}).passthrough();
export type PrivateMappings = z.infer<typeof privateMappingsSchema>;
